import { ONBOARDING_READ_ROLES, inPeopleContentCohort } from './people-content-cohorts';
/**
 * People/Workforce Phase 2 — pure helpers for People list, Worker 360,
 * lifecycle cohorts, document register labels, and compatibility redirects.
 *
 * Source contract: docs/source-of-truth/people-workforce-phase2-contract.md
 * SQL owner: supabase/migrations/20270403000000_people_workforce_phase2_people_worker360.sql
 *
 * Wave 2 is additive over domain owners. No new write path. No global
 * compliant/ready badge. No transactional roster authority.
 */

import { compareInstantsDesc } from '@statura/utils';
import {
  WORKFORCE_CAPABILITY_ROLE_GRANTS,
  type WorkforcePhase1aCapabilityId,
} from './phase1a-capabilities';
import {
  WORKFORCE_PEOPLE_VIEW_ROLES,
  isAllowlistedDeepLink,
  obligationRequirementTitle,
  personScopedObligationAction,
  type WorkerObligationItem,
  type WorkerObligationStatus,
} from './phase1b-obligations';

export { WORKFORCE_PEOPLE_VIEW_ROLES };

/** Stable Worker 360 tab ids — order is contract-stable. */
export const WORKER_360_TABS = [
  'overview',
  'employment',
  'onboarding',
  'compliance',
  'learning',
  'policies',
  'development',
  'documents',
  'history',
] as const;

export type Worker360Tab = (typeof WORKER_360_TABS)[number];

export const WORKER_360_TAB_LABELS: Record<Worker360Tab, string> = {
  overview: 'Overview',
  employment: 'Employment and access',
  onboarding: 'Onboarding',
  compliance: 'Compliance and credentials',
  learning: 'Learning and competencies',
  policies: 'Policies and Conduct',
  development: 'Development',
  documents: 'Documents',
  history: 'History and offboarding',
};

export function isWorker360Tab(value: string | null | undefined): value is Worker360Tab {
  return !!value && (WORKER_360_TABS as readonly string[]).includes(value);
}

export function parseWorker360Tab(value: string | null | undefined): Worker360Tab {
  // Wave 4 canonical deep-link alias for the Policies and Conduct panel.
  if (value === 'policies-conduct' || value === 'policies_conduct') return 'policies';
  return isWorker360Tab(value) ? value : 'overview';
}

/**
 * Lifecycle cohorts for the People list.
 * PRE_HIRE / ACCEPTED are never inferred from an active staff row.
 */
export const WORKER_LIFECYCLE_COHORTS = [
  'PRE_HIRE',
  'ACCEPTED',
  'AWAITING_ONBOARDING',
  'NO_CURRENT_ENGAGEMENT',
  'ONBOARDING',
  'ACTIVE',
  'SUSPENDED',
  'EXCLUDED',
  'OFFBOARDING',
  'TERMINATED',
  'INACTIVE',
  'DEGRADED',
] as const;

export type WorkerLifecycleCohort = (typeof WORKER_LIFECYCLE_COHORTS)[number];

export const WORKER_LIFECYCLE_LABELS: Record<WorkerLifecycleCohort, string> = {
  PRE_HIRE: 'Pre-hire',
  ACCEPTED: 'Accepted engagement',
  AWAITING_ONBOARDING: 'Awaiting onboarding',
  NO_CURRENT_ENGAGEMENT: 'No current engagement',
  ONBOARDING: 'Onboarding',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  EXCLUDED: 'Excluded',
  OFFBOARDING: 'Offboarding',
  TERMINATED: 'Left',
  INACTIVE: 'Inactive',
  DEGRADED: 'Unable to determine',
};

export type LifecycleSourceFacts = {
  /** True only when a non-staff pre-hire/candidate source row exists. */
  hasPreHireSource?: boolean;
  preHireState?: string | null;
  /** True only for accepted-offer conversion without staff (not inventable from staff). */
  hasAcceptedEngagementWithoutStaff?: boolean;
  hasStaffRow: boolean;
  staffIsActive?: boolean | null;
  staffEndDate?: string | null;
  staffStartDate?: string | null;
  /** False only after a successful history read confirms no commenced engagement. */
  hasCommencedEngagement?: boolean;
  asOf?: string | null;
  engagementLifecycleState?: string | null;
  engagementClassificationState?: string | null;
  engagementAccountability?: string | null;
  onboardingStatus?: string | null;
  offboardingStatus?: string | null;
  /** Explicit exclusion / banning source flag when known. */
  isExcluded?: boolean | null;
  ndisScreeningOutcome?: string | null;
};

/**
 * Conservative lifecycle derivation.
 * Unknown conflicts → DEGRADED. Never invent PRE_HIRE from staff.
 */
export function deriveWorkerLifecycleCohort(facts: LifecycleSourceFacts): WorkerLifecycleCohort {
  // Candidate/offer rows stop owning lifecycle once a staff record exists.
  // A conversion may temporarily leave both sources present; staff truth wins.
  if (facts.hasPreHireSource && !facts.hasStaffRow) {
    const state = (facts.preHireState ?? '').trim().toUpperCase();
    if (state === 'ACCEPTED' || state === 'OFFER_ACCEPTED') return 'ACCEPTED';
    return 'PRE_HIRE';
  }
  if (facts.hasAcceptedEngagementWithoutStaff && !facts.hasStaffRow) return 'ACCEPTED';

  if (!facts.hasStaffRow) return 'DEGRADED';

  const engagement = (facts.engagementLifecycleState ?? '').trim().toUpperCase();
  const onboarding = (facts.onboardingStatus ?? '').trim().toUpperCase();
  const offboarding = (facts.offboardingStatus ?? '').trim().toUpperCase();
  const classification = (facts.engagementClassificationState ?? '').trim().toUpperCase();
  const ndisOutcome = (facts.ndisScreeningOutcome ?? '').trim().toLowerCase();

  const signals: WorkerLifecycleCohort[] = [];

  if (engagement === 'TERMINATED') signals.push('TERMINATED');
  const endDatePassed = Boolean(
    facts.staffEndDate && facts.asOf && facts.staffEndDate <= facts.asOf,
  );
  if (endDatePassed) signals.push('TERMINATED');
  if (offboarding === 'IN_PROGRESS' || offboarding === 'STALLED' || engagement === 'OFFBOARDING') {
    signals.push('OFFBOARDING');
  }
  if (facts.isExcluded === true || ndisOutcome === 'excluded') {
    signals.push('EXCLUDED');
  }
  if (engagement === 'SUSPENDED') signals.push('SUSPENDED');
  if (onboarding === 'IN_PROGRESS' || onboarding === 'STALLED') signals.push('ONBOARDING');

  // Open offboarding is follow-up work, not uncertainty about a recorded departure.
  if (signals.includes('TERMINATED') && signals.includes('ONBOARDING') && !endDatePassed)
    return 'DEGRADED';
  if (signals.includes('TERMINATED')) return 'TERMINATED';
  if (signals.includes('OFFBOARDING')) return 'OFFBOARDING';
  if (signals.includes('EXCLUDED')) return 'EXCLUDED';
  if (signals.includes('SUSPENDED')) return 'SUSPENDED';
  if (signals.includes('ONBOARDING')) return 'ONBOARDING';

  if (
    facts.staffIsActive === false &&
    !facts.staffEndDate &&
    facts.asOf &&
    ['', 'LEGACY_UNKNOWN', 'ACTIVE'].includes(engagement) &&
    ['', 'COMPLETED', 'ABANDONED'].includes(onboarding) &&
    ['', 'COMPLETED', 'ABANDONED'].includes(offboarding)
  )
    return 'INACTIVE';

  // Mirror the SQL helper: unknown facts stay unresolved rather than becoming a cohort.
  if (
    !['', 'COMPLETED', 'ABANDONED'].includes(onboarding) ||
    !['', 'COMPLETED', 'ABANDONED'].includes(offboarding) ||
    !['', 'ACTIVE', 'LEGACY_UNKNOWN'].includes(engagement) ||
    facts.staffIsActive !== true ||
    !facts.asOf ||
    (facts.staffEndDate && facts.staffEndDate <= facts.asOf)
  )
    return 'DEGRADED';

  if (
    engagement === '' ||
    engagement === 'LEGACY_UNKNOWN' ||
    classification === 'LEGACY_UNCLASSIFIED'
  ) {
    // The controlled list preserves real new hires using this same 30-day window.
    if (
      facts.hasCommencedEngagement === false &&
      onboarding === '' &&
      ['', 'ABANDONED'].includes(offboarding) &&
      facts.staffStartDate &&
      Date.parse(facts.staffStartDate) >= Date.parse(facts.asOf) - 30 * 86_400_000
    )
      return 'AWAITING_ONBOARDING';
    return 'NO_CURRENT_ENGAGEMENT';
  }
  if (engagement === 'ACTIVE') return 'ACTIVE';

  return 'DEGRADED';
}

/** Obligation statuses that count as manager action-required. */
const ACTION_REQUIRED_STATUSES: ReadonlySet<WorkerObligationStatus> = new Set([
  'PENDING',
  'AWAITING_EVIDENCE',
  'AWAITING_VERIFICATION',
  'OVERDUE',
  'EXPIRED',
  'REJECTED',
  'SUSPENDED',
  'DEGRADED',
  'DUE_SOON',
]);

export function isActionRequiredObligationStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return ACTION_REQUIRED_STATUSES.has(status.toUpperCase() as WorkerObligationStatus);
}

/**
 * Count action-required obligations per worker from the approved register.
 * Does not invent a global compliant/ready badge.
 */
export function actionRequiredCountByWorker(
  items: ReadonlyArray<Pick<WorkerObligationItem, 'staff_id' | 'status'>>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (!item.staff_id || !isActionRequiredObligationStatus(item.status)) continue;
    counts.set(item.staff_id, (counts.get(item.staff_id) ?? 0) + 1);
  }
  return counts;
}

export function totalActionRequiredObligations(
  items: ReadonlyArray<Pick<WorkerObligationItem, 'status'>>,
): number {
  return items.filter((i) => isActionRequiredObligationStatus(i.status)).length;
}

/** Document access classifications — never invent a merged evidence id. */
export const WORKER_DOCUMENT_ACCESS_CLASSES = [
  'STANDARD',
  'RESTRICTED_PAYROLL',
  'RESTRICTED_HEALTH',
  'RESTRICTED_CONDUCT',
  'MANAGER_REVIEW',
  'WORKER_SELF',
  'UNKNOWN',
] as const;

export type WorkerDocumentAccessClass = (typeof WORKER_DOCUMENT_ACCESS_CLASSES)[number];

export type WorkerDocumentRegisterItem = {
  category_display_name?: string | null;
  source_table: string;
  source_id: string;
  category: string | null;
  owner_label: string | null;
  status: string | null;
  version: string | number | null;
  expiry_date: string | null;
  access_classification: WorkerDocumentAccessClass;
  signature_state: string | null;
  verification_state: string | null;
  /** DMS B1.14 canonical object lifecycle state for onboarding rows. */
  dms_object_state: string | null;
  /** Download readiness; PDF_PENDING is intentionally not a download link. */
  download_state: string | null;
  freshness_at: string | null;
  /** Source-labelled only; never a fabricated unified evidence id. */
  evidence_label: string;
};

const RESTRICTED_ONBOARDING_CATEGORIES = new Set(['TAX_SUPER_BANK_FORMS']);

export function classifyOnboardingDocumentAccess(
  category: string | null | undefined,
): WorkerDocumentAccessClass {
  const cat = (category ?? '').toUpperCase();
  if (RESTRICTED_ONBOARDING_CATEGORIES.has(cat)) return 'RESTRICTED_PAYROLL';
  if (cat === 'CLINICAL_SCOPE_OF_PRACTICE') return 'RESTRICTED_HEALTH';
  if (cat.includes('CONDUCT')) return 'RESTRICTED_CONDUCT';
  return 'MANAGER_REVIEW';
}

export function mapOnboardingDocumentToRegisterItem(row: {
  id: string;
  document_category?: string | null;
  status?: string | null;
  template_version_at_assignment?: number | null;
  vevo_expiry_date?: string | null;
  manager_verified_at?: string | null;
  signature_signed_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  dms_object_state?: string | null;
  download_state?: string | null;
}): WorkerDocumentRegisterItem {
  const category = row.document_category ?? null;
  const status = row.status ?? null;
  const signatureState = row.signature_signed_at
    ? 'SIGNED'
    : status === 'PENDING'
      ? 'UNSIGNED'
      : status;
  const verificationState = row.manager_verified_at
    ? 'VERIFIED'
    : status === 'VERIFIED'
      ? 'VERIFIED'
      : status === 'REJECTED'
        ? 'REJECTED'
        : 'UNVERIFIED';

  return {
    source_table: 'worker_onboarding_documents',
    source_id: row.id,
    category,
    owner_label: 'Onboarding document',
    status,
    version: row.template_version_at_assignment ?? null,
    expiry_date: row.vevo_expiry_date ?? null,
    access_classification: classifyOnboardingDocumentAccess(category),
    signature_state: signatureState ?? null,
    verification_state: verificationState,
    dms_object_state: row.dms_object_state ?? null,
    download_state: row.download_state ?? null,
    freshness_at: row.updated_at ?? row.created_at ?? null,
    evidence_label: `worker_onboarding_documents:${row.id}`,
  };
}

export function mapGenericDocumentToRegisterItem(row: {
  id: string;
  document_type?: string | null;
  status?: string | null;
  version?: string | null;
  expiry_date?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  entity_type?: string | null;
}): WorkerDocumentRegisterItem {
  return {
    source_table: 'documents',
    source_id: row.id,
    category: row.document_type ?? null,
    owner_label: 'Generic document',
    status: row.status ?? null,
    version: row.version ?? null,
    expiry_date: row.expiry_date ?? null,
    // The generic documents source has no worker-specific sensitivity field.
    // Do not invent a STANDARD classification from document_type alone.
    access_classification: 'UNKNOWN',
    signature_state: null,
    verification_state: null,
    dms_object_state: null,
    download_state: null,
    freshness_at: row.updated_at ?? row.created_at ?? null,
    evidence_label: `documents:${row.id}`,
  };
}

/** Manager queue name for onboarding-only document review. */
export const ONBOARDING_DOCUMENT_REVIEW_LABEL = 'Onboarding document review';

/** Canonical Wave 2 manager primary nav destinations. */
export const PHASE2_PRIMARY_NAV = {
  workforceOverview: { label: 'People', href: '/people' },
  people: { label: 'Workers', href: '/people/workers' },
  onboarding: { label: 'Onboarding', href: '/people/onboarding' },
  offboarding: { label: 'Offboarding', href: '/people/offboarding' },
} as const;

export function worker360Path(staffId: string, tab?: Worker360Tab, extra?: Record<string, string>) {
  const params = new URLSearchParams();
  if (tab && tab !== 'overview') params.set('tab', tab);
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v) params.set(k, v);
    }
  }
  const q = params.toString();
  const encodedStaffId = encodeURIComponent(staffId);
  return q ? `/people/workers/${encodedStaffId}?${q}` : `/people/workers/${encodedStaffId}`;
}

export function peopleWorkersPath(filters?: Record<string, string | undefined>) {
  if (!filters) return '/people/workers';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v != null && v !== '') params.set(k, v);
  }
  const q = params.toString();
  return q ? `/people/workers?${q}` : '/people/workers';
}

/** Safe query keys preserved across compatibility redirects. */
export const COMPAT_REDIRECT_SAFE_PARAMS = [
  'tab',
  'focus',
  'search',
  'status',
  'lifecycle',
  'lifecycle_state',
  'facility_id',
  'region_id',
  'role',
  'engagement_type',
  'page',
  'page_size',
  'staffId',
  'staff_id',
  'documentId',
  'journeyId',
  'requirement_type',
  'urgency_group',
  'include_current',
  'owner',
  'authority_basis',
  'impact',
] as const;

export function pickSafeRedirectParams(
  searchParams: URLSearchParams | Record<string, string | string[] | undefined>,
): URLSearchParams {
  const out = new URLSearchParams();
  const getAll = (key: string): string[] => {
    if (searchParams instanceof URLSearchParams) {
      return searchParams.getAll(key);
    }
    const raw = searchParams[key];
    if (raw == null) return [];
    return Array.isArray(raw) ? raw : [raw];
  };

  let acceptedValues = 0;
  for (const key of COMPAT_REDIRECT_SAFE_PARAMS) {
    for (const value of getAll(key)) {
      if (!value) continue;
      if (acceptedValues >= 30 || value.length > 200) continue;
      // Reject absolute / protocol-relative return URLs smuggled as values.
      if (/^(https?:|\/\/|javascript:|data:)/i.test(value)) continue;
      if (key === 'tab' && !isWorker360Tab(value)) continue;
      if (
        ['facility_id', 'region_id', 'staffId', 'staff_id', 'documentId', 'journeyId'].includes(
          key,
        ) &&
        !isCanonicalUuid(value)
      ) {
        continue;
      }
      out.append(key, value);
      acceptedValues += 1;
    }
  }
  return out;
}

export function buildCompatibilityRedirect(
  canonicalPath: string,
  searchParams: URLSearchParams | Record<string, string | string[] | undefined>,
): string {
  if (!canonicalPath.startsWith('/') || /^(https?:|\/\/)/i.test(canonicalPath)) {
    throw new Error('invalid_canonical_path');
  }
  // Reject open redirects.
  if (canonicalPath.includes('//') || canonicalPath.includes('\\')) {
    throw new Error('invalid_canonical_path');
  }
  const safe = pickSafeRedirectParams(searchParams);
  const q = safe.toString();
  return q ? `${canonicalPath}?${q}` : canonicalPath;
}

/** Capability gate: role holds named workforce capability (Phase 1A mirror). */
export function roleHasWorkforceCapability(
  role: string | null | undefined,
  capability: WorkforcePhase1aCapabilityId,
): boolean {
  const r = (role ?? '').toUpperCase();
  const grants = WORKFORCE_CAPABILITY_ROLE_GRANTS[capability] ?? [];
  return (grants as readonly string[]).includes(r);
}

/**
 * Worker 360 section visibility — never broader than existing capability cohorts.
 * Payroll/health/conduct-case/detailed performance remain restricted.
 */
export type Worker360SectionAccess = {
  canViewPeople: boolean;
  canViewEmployment: boolean;
  canViewOnboarding: boolean;
  canViewCompliance: boolean;
  canViewLearning: boolean;
  canViewPolicies: boolean;
  canViewConductCases: boolean;
  canViewDevelopmentSummary: boolean;
  canViewDocuments: boolean;
  canViewPayrollFields: boolean;
  canViewOffboarding: boolean;
  isSelf: boolean;
};

export function resolveWorker360SectionAccess(input: {
  role: string | null | undefined;
  isSelf: boolean;
  hasLearningModule?: boolean;
}): Worker360SectionAccess {
  const role = input.role;
  const canViewPeople = input.isSelf || roleHasWorkforceCapability(role, 'workforce.people.view');
  const canViewOnboarding = inPeopleContentCohort(role, ONBOARDING_READ_ROLES);
  const canManagePerformance = roleHasWorkforceCapability(role, 'workforce.performance.manage');
  const canManageConduct = roleHasWorkforceCapability(role, 'workforce.conduct.manage');
  const canVerifyDocs = roleHasWorkforceCapability(role, 'workforce.documents.verify');
  const canOffboard = roleHasWorkforceCapability(role, 'workforce.offboarding.manage');

  // Payroll field access stays with finance/admin cohorts already used on pay-profile.
  // Exact parity with the retained /staff/[id]/pay-profile production gate.
  const payrollRoles = new Set([
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FINANCE_OFFICER',
    'FACILITY_MANAGER',
    'PAYROLL_OFFICER',
  ]);
  const canViewPayrollFields = payrollRoles.has((role ?? '').toUpperCase());

  return {
    canViewPeople,
    canViewEmployment: canViewPeople,
    canViewOnboarding: canViewOnboarding || input.isSelf,
    canViewCompliance: canViewPeople || input.isSelf,
    canViewLearning: (canViewPeople || input.isSelf) && input.hasLearningModule !== false,
    canViewPolicies: canViewPeople || input.isSelf,
    canViewConductCases: canManageConduct,
    canViewDevelopmentSummary: canViewPeople || canManagePerformance || input.isSelf,
    canViewDocuments: canVerifyDocs || input.isSelf,
    canViewPayrollFields,
    canViewOffboarding: canOffboard,
    isSelf: input.isSelf,
  };
}

/** Timeline events only when source provides a real timestamp. */
export type WorkerTimelineEvent = {
  source_table: string;
  source_id: string;
  event_type: string;
  occurred_at: string;
  summary: string;
};

export function buildTimelineFromSourceEvents(
  events: Array<{
    source_table: string;
    source_id: string;
    event_type?: string | null;
    occurred_at?: string | null;
    summary?: string | null;
  }>,
): WorkerTimelineEvent[] {
  const out: WorkerTimelineEvent[] = [];
  for (const e of events) {
    if (!e.occurred_at || !e.source_table || !e.source_id) continue;
    // Reject undated fabrications.
    if (Number.isNaN(Date.parse(e.occurred_at))) continue;
    out.push({
      source_table: e.source_table,
      source_id: e.source_id,
      event_type: e.event_type || 'EVENT',
      occurred_at: e.occurred_at,
      summary: e.summary || e.event_type || 'Source event',
    });
  }
  // Newest-first sets the reading order of the Worker 360 timeline. The events are
  // fanned in from several different `source_table`s, so the timestamps do not share
  // one producer and a text compare would order `…+00:00` before `…Z` for the same
  // instant. The loop above already `Date.parse`-validates every value, so ordering
  // on the parsed instant is both correct and free.
  return out.sort((a, b) => compareInstantsDesc(a.occurred_at, b.occurred_at));
}

export function filterObligationsForTab(
  items: WorkerObligationItem[],
  tab: Worker360Tab,
): WorkerObligationItem[] {
  const typeOf = (item: WorkerObligationItem) => (item.requirement_type ?? '').toUpperCase();
  switch (tab) {
    case 'overview':
      return items;
    case 'onboarding':
      return items.filter((i) =>
        ['ONBOARDING', 'LOGIN', 'FIRST_SHIFT', 'ENGAGEMENT'].includes(typeOf(i)),
      );
    case 'compliance':
      return items.filter((i) =>
        [
          'SCREENING',
          'CERTIFICATION',
          'WORK_RIGHTS',
          'REGISTRATION',
          'LICENCE',
          'QUALIFICATION',
        ].includes(typeOf(i)),
      );
    case 'learning':
      return items.filter((i) => ['TRAINING', 'COMPETENCY'].includes(typeOf(i)));
    case 'policies':
      return items.filter((i) => ['POLICY_ACK', 'CODE'].includes(typeOf(i)));
    case 'development':
      return items.filter((i) => ['PROBATION', 'PERFORMANCE', 'SUPERVISION'].includes(typeOf(i)));
    default:
      return [];
  }
}

export type Worker360ScoreboardBucket = 'waiting_on_them' | 'waiting_on_you' | 'still_needed';

export type Worker360ScoreboardDomain = 'onboarding' | 'learning' | 'policies' | 'credentials';

export type Worker360ScoreboardRow = {
  obligationKey: string;
  title: string;
  domain: Worker360ScoreboardDomain;
  domainLabel: string;
  bucket: Worker360ScoreboardBucket;
  href: string;
};

export type Worker360Scoreboard = {
  waitingOnThem: Worker360ScoreboardRow[];
  waitingOnYou: Worker360ScoreboardRow[];
  stillNeeded: Worker360ScoreboardRow[];
  currentCount: number;
};

const SCOREBOARD_DOMAIN_LABELS: Record<Worker360ScoreboardDomain, string> = {
  onboarding: 'Onboarding',
  learning: 'Training',
  policies: 'Policies',
  credentials: 'Credentials',
};

function scoreboardDomain(item: WorkerObligationItem): Worker360ScoreboardDomain {
  const type = (item.requirement_type ?? '').toUpperCase();
  if (['TRAINING', 'COMPETENCY'].includes(type)) return 'learning';
  if (['POLICY_ACK', 'CODE'].includes(type)) return 'policies';
  if (
    [
      'SCREENING',
      'CERTIFICATION',
      'WORK_RIGHTS',
      'REGISTRATION',
      'LICENCE',
      'QUALIFICATION',
    ].includes(type)
  ) {
    return 'credentials';
  }
  return 'onboarding';
}

function scoreboardTitle(item: WorkerObligationItem): string {
  return obligationRequirementTitle(item);
}

function scoreboardBucket(item: WorkerObligationItem): Worker360ScoreboardBucket {
  const action = String(item.next_action?.type ?? '').toUpperCase();
  const status = String(item.status ?? '').toUpperCase();
  if (action === 'REVIEW_EVIDENCE' || action === 'VERIFY' || status === 'AWAITING_VERIFICATION') {
    return 'waiting_on_you';
  }
  if (
    action === 'COLLECT_EVIDENCE' ||
    action === 'ACKNOWLEDGE_POLICY' ||
    action === 'COMPLETE_TRAINING' ||
    action === 'COMPLETE_ONBOARDING' ||
    action === 'LINK_LOGIN' ||
    status === 'AWAITING_EVIDENCE'
  ) {
    return 'waiting_on_them';
  }
  return 'still_needed';
}

export function buildWorker360Scoreboard(
  items: WorkerObligationItem[],
  staffId: string,
): Worker360Scoreboard {
  const waitingOnThem: Worker360ScoreboardRow[] = [];
  const waitingOnYou: Worker360ScoreboardRow[] = [];
  const stillNeeded: Worker360ScoreboardRow[] = [];
  let currentCount = 0;

  for (const item of items) {
    const status = String(item.status ?? '').toUpperCase();
    if (status === 'CURRENT' || status === 'WAIVED' || status === 'NOT_APPLICABLE') {
      currentCount += 1;
      continue;
    }
    if (!isActionRequiredObligationStatus(item.status)) continue;

    const domain = scoreboardDomain(item);
    const bucket = scoreboardBucket(item);
    const tab: Worker360Tab =
      domain === 'credentials' ? 'compliance' : domain === 'onboarding' ? 'onboarding' : domain;
    const scopedHref = personScopedObligationAction(item).href;
    const href = isAllowlistedDeepLink(scopedHref) ? scopedHref : worker360Path(staffId, tab);
    const row: Worker360ScoreboardRow = {
      obligationKey: item.obligation_key,
      title: scoreboardTitle(item),
      domain,
      domainLabel: SCOREBOARD_DOMAIN_LABELS[domain],
      bucket,
      href,
    };
    if (bucket === 'waiting_on_you') waitingOnYou.push(row);
    else if (bucket === 'waiting_on_them') waitingOnThem.push(row);
    else stillNeeded.push(row);
  }

  return { waitingOnThem, waitingOnYou, stillNeeded, currentCount };
}

export function topBlockers(items: WorkerObligationItem[], limit = 5): WorkerObligationItem[] {
  const priority = (status: string) => {
    const s = status.toUpperCase();
    if (s === 'SUSPENDED' || s === 'EXPIRED' || s === 'REJECTED') return 0;
    if (s === 'OVERDUE') return 1;
    if (s === 'DEGRADED') return 2;
    if (s === 'AWAITING_VERIFICATION' || s === 'AWAITING_EVIDENCE') return 3;
    if (s === 'PENDING' || s === 'DUE_SOON') return 4;
    return 9;
  };
  return [...items]
    .filter((i) => isActionRequiredObligationStatus(i.status))
    .sort((a, b) => {
      const p = priority(a.status) - priority(b.status);
      if (p !== 0) return p;
      return String(a.obligation_key).localeCompare(String(b.obligation_key));
    })
    .slice(0, limit);
}

export function parsePeopleListFilters(
  searchParams: Record<string, string | string[] | undefined>,
): {
  page: number;
  page_size: number;
  lifecycle?: WorkerLifecycleCohort;
  facility_id?: string;
  region_id?: string;
  role?: string;
  engagement_type?: string;
  search?: string;
} {
  const one = (key: string) => {
    const v = searchParams[key];
    if (Array.isArray(v)) return v[0];
    return v;
  };
  const page = Math.max(1, Math.floor(Number(one('page') ?? 1)) || 1);
  const page_size = Math.min(100, Math.max(1, Math.floor(Number(one('page_size') ?? 25)) || 25));
  const lifecycleRaw = (one('lifecycle') ?? one('lifecycle_state') ?? '').toUpperCase();
  const lifecycle = (WORKER_LIFECYCLE_COHORTS as readonly string[]).includes(lifecycleRaw)
    ? (lifecycleRaw as WorkerLifecycleCohort)
    : undefined;
  const facility_id = one('facility_id') || undefined;
  const region_id = one('region_id') || undefined;
  const role = one('role') || undefined;
  const engagement_type = one('engagement_type') || undefined;
  const search = one('search')?.trim() || undefined;

  if (facility_id && !isCanonicalUuid(facility_id)) {
    throw new Error('Invalid facility_id filter');
  }
  if (region_id && !isCanonicalUuid(region_id)) {
    throw new Error('Invalid region_id filter');
  }

  return { page, page_size, lifecycle, facility_id, region_id, role, engagement_type, search };
}

export function isCanonicalUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function paginatePeopleRows<T extends { staff_id: string; display_name?: string | null }>(
  rows: T[],
  page: number,
  pageSize: number,
): { items: T[]; page: number; page_size: number; worker_count: number } {
  const safePage = Math.max(1, Math.floor(page) || 1);
  const safeSize = Math.min(100, Math.max(1, Math.floor(pageSize) || 25));
  const sorted = [...rows].sort((a, b) => {
    const nameA = (a.display_name ?? a.staff_id).toLowerCase();
    const nameB = (b.display_name ?? b.staff_id).toLowerCase();
    const c = nameA.localeCompare(nameB);
    if (c !== 0) return c;
    return a.staff_id.localeCompare(b.staff_id);
  });
  const start = (safePage - 1) * safeSize;
  return {
    items: sorted.slice(start, start + safeSize),
    page: safePage,
    page_size: safeSize,
    worker_count: sorted.length,
  };
}

/** Follow-up only: journey work never changes the recorded departure cohort. */
export function leaverJourneyActions(
  lifecycle: string,
  onboarding: string | null,
  offboarding: string | null,
) {
  if (lifecycle !== 'TERMINATED') return [];
  return [
    ...(['IN_PROGRESS', 'STALLED'].includes(offboarding ?? '') ? ['Offboarding still open'] : []),
    ...(['IN_PROGRESS', 'STALLED'].includes(onboarding ?? '') ? ['Onboarding still open'] : []),
  ];
}
